import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { Colliders } from './Colliders';

/**
 * Fetch. Hold to charge, release to throw a ball or a stick from where you
 * stand (the camera). The toy flies, bounces and rolls with simple physics;
 * she chases it, sometimes catches it on the bounce, carries it back, and
 * drops it near you — or a bit further away when she's feeling cheeky.
 */
export type ToyKind = 'ball' | 'stick' | 'treat' | 'frisbee' | 'labubu';
type State = 'idle' | 'flying' | 'chasing' | 'reaching' | 'returning';

const G = 9.81;
const _v = new THREE.Vector3();
const _p = new THREE.Vector3();

export class Fetch {
  kind: ToyKind = 'ball';
  onMessage?: (text: string) => void;
  onFetched?: () => void;
  /** the toy hit something solid (name from the collider) */
  onHit?: (name: string, speed: number) => void;
  colliders: Colliders | null = null;
  private lastHitAt = 0;
  state: State = 'idle';
  private toy: THREE.Group;
  private ball: THREE.Mesh;
  private stick: THREE.Mesh;
  private treat: THREE.Mesh;
  private frisbee: THREE.Group;
  private labubu: THREE.Mesh;
  /** she caught it in the air (the frisbee's point) */
  onCaught?: (kind: ToyKind) => void;
  /** she ate a thrown treat */
  onTreatEaten?: () => void;
  readonly vel = new THREE.Vector3();
  private spin = new THREE.Vector3();
  inHand = true;
  carried = false;
  private retargetAt = 0;
  /** where the last throw left your hand, and how far she went for it */
  private thrownFrom = new THREE.Vector3();
  lastDistance = 0;
  private eatAt = 0;
  private restTimer = 0;
  private catchChance = 0.35;

  constructor(
    scene: THREE.Object3D,
    private dog: Dog,
  ) {
    this.toy = new THREE.Group();
    this.toy.name = 'toy';
    this.toy.userData.tapName = 'toy';
    this.ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.065, 16, 12),
      new THREE.MeshStandardMaterial({ color: 0xd9e84a, roughness: 0.8 }),
    );
    this.ball.castShadow = true;
    this.stick = new THREE.Mesh(
      new THREE.CylinderGeometry(0.018, 0.024, 0.38, 8),
      new THREE.MeshStandardMaterial({ color: 0x7a5533, roughness: 1 }),
    );
    this.stick.rotation.z = Math.PI / 2;
    this.stick.castShadow = true;
    this.treat = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.02, 0.06, 4, 8),
      new THREE.MeshStandardMaterial({ color: 0xe8d3a8, roughness: 0.8 }),
    );
    this.treat.rotation.z = Math.PI / 2;
    this.treat.castShadow = true;
    // frisbee: a flat orange disc with a rolled rim
    this.frisbee = new THREE.Group();
    const discMat = new THREE.MeshStandardMaterial({ color: 0xf07a2a, roughness: 0.55 });
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.115, 0.012, 28), discMat);
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.115, 0.012, 8, 28), discMat);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = -0.004;
    disc.castShadow = true;
    this.frisbee.add(disc, rim);
    // Labubu: Annabelle's doll as a cutout card — it tumbles through the air and lands flat
    const labubuTex = new THREE.TextureLoader().load('toys/labubu.png');
    labubuTex.colorSpace = THREE.SRGBColorSpace;
    this.labubu = new THREE.Mesh(new THREE.PlaneGeometry(0.19, 0.25), new THREE.MeshStandardMaterial({ map: labubuTex, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 1 }));
    this.labubu.castShadow = true;
    this.labubu.customDepthMaterial = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: labubuTex, alphaTest: 0.5 });
    this.toy.add(this.ball, this.stick, this.treat, this.frisbee, this.labubu);
    this.toy.visible = false;
    scene.add(this.toy);
    this.setKind('ball');
  }

  get root(): THREE.Object3D {
    return this.toy;
  }

  get velocity(): THREE.Vector3 {
    return this.vel;
  }

  setKind(k: ToyKind): void {
    this.kind = k;
    this.ball.visible = k === 'ball';
    this.stick.visible = k === 'stick';
    this.treat.visible = k === 'treat';
    this.frisbee.visible = k === 'frisbee';
    this.labubu.visible = k === 'labubu';
  }

  /** a fresh copy of a toy for showing in your hand (shares geometry and material) */
  heldCopy(kind: ToyKind): THREE.Object3D {
    const src = kind === 'ball' ? this.ball : kind === 'stick' ? this.stick : kind === 'frisbee' ? this.frisbee : kind === 'labubu' ? this.labubu : this.treat;
    const c = src.clone();
    c.visible = true;
    return c;
  }

  /** the toy comes back to your hand (tap it on the ground, or start a throw) */
  takeBack(): void {
    if (this.carried) this.dog.carry(null);
    this.carried = false;
    this.inHand = true;
    this.toy.visible = false;
    this.state = 'idle';
  }

  /** @param seconds how long the throw was charged; @param at ground point aimed at */
  throw(from: THREE.Vector3, at: THREE.Vector3, seconds: number): void {
    this.takeBack();
    const power = THREE.MathUtils.clamp((seconds - 0.2) / 1.3, 0.1, 1);
    // launch from your hand toward the aim point; distance from charge, direction from aim
    _v.subVectors(at, from).setY(0);
    const dir = _v.lengthSq() > 1e-4 ? _v.normalize() : _v.set(0, 0, -1);
    const dist = 1.5 + power * (this.kind === 'ball' ? 6 : this.kind === 'stick' ? 4.5 : this.kind === 'frisbee' ? 9 : this.kind === 'labubu' ? 4.5 : 3.5);
    const angle = this.kind === 'ball' ? 0.6 : this.kind === 'frisbee' ? 0.3 : 0.5; // rad
    const h = 1.0;
    // speed so a flat-ground projectile from height h lands at `dist`
    const sin2 = Math.sin(2 * angle);
    const v0 = Math.sqrt((G * dist * dist) / (dist * sin2 + 2 * h * Math.cos(angle) ** 2));
    this.vel.copy(dir).multiplyScalar(v0 * Math.cos(angle));
    this.vel.y = v0 * Math.sin(angle);
    this.thrownFrom.copy(from).setY(0);
    this.toy.position.copy(from).setY(h).addScaledVector(dir, 0.3);
    this.toy.visible = true;
    this.inHand = false;
    this.spin.set(Math.random() - 0.5, Math.random() - 0.5, 1).normalize().multiplyScalar(this.kind === 'stick' ? 12 : 20);
    this.state = 'flying';
    this.restTimer = 0;
    this.retargetAt = 0;
    if (this.kind === 'frisbee') {
      // a disc glides: the lift below makes the speed the launch solver picked carry further, so ease off
      this.vel.multiplyScalar(0.85);
      this.toy.rotation.set(0, 0, 0);
    }
    this.onMessage?.(this.kind === 'ball' ? 'ball!' : this.kind === 'stick' ? 'stick!' : this.kind === 'frisbee' ? 'frisbee!' : this.kind === 'labubu' ? 'Labubu!' : 'treat!');
    this.dog.notice(this.toy.position);
    this.dog.excitement = 1;
  }

  /** something else took over (a tap, a command, a squirrel): stop chasing, leave the toy */
  cancel(): void {
    if (this.state === 'idle') return;
    if (this.carried) {
      this.dropHere();
    }
    this.state = this.inHand ? 'idle' : 'idle';
  }

  private dropHere(): void {
    this.dog.carry(null);
    this.carried = false;
    // place on the ground just ahead of her nose
    _v.set(0, 0, 0.45 * this.dog.profile.size).applyAxisAngle(new THREE.Vector3(0, 1, 0), this.dog.root.rotation.y);
    this.toy.position.copy(this.dog.root.position).add(_v);
    this.toy.position.y = this.kind === 'ball' ? 0.065 : this.kind === 'labubu' ? 0.05 : 0.024;
    this.toy.rotation.set(0, Math.random() * Math.PI, 0);
    if (this.kind === 'labubu') this.toy.rotation.set(-Math.PI / 2, 0, Math.random() * Math.PI);
    this.vel.set(0, 0, 0);
    this.toy.visible = true;
  }

  update(dt: number, you: THREE.Vector3): void {
    const toy = this.toy;
    const dog = this.dog;

    // --- toy physics (whenever it's loose)
    if (!this.inHand && !this.carried) {
      const r = this.kind === 'ball' ? 0.065 : this.kind === 'stick' ? 0.024 : this.kind === 'labubu' ? 0.05 : 0.02;
      const airborne = toy.position.y > r + 0.002 || this.vel.y > 0.01;
      if (airborne) {
        // a frisbee in fast flight glides: lift cancels most of gravity while it's moving
        const glide = this.kind === 'frisbee' && this.vel.lengthSq() > 4 ? 0.3 : 1;
        this.vel.y -= G * glide * dt;
        if (this.kind === 'frisbee') {
          // lies flat, nose slightly up, banking a little to the side
          const heading = Math.atan2(this.vel.x, this.vel.z);
          this.toy.rotation.set(-0.15, heading, 0.25 * Math.sin(this.restTimer * 2.5), 'YXZ');
        }
        toy.position.addScaledVector(this.vel, dt);
        toy.rotation.x += this.spin.x * dt;
        toy.rotation.y += this.spin.y * dt;
        toy.rotation.z += this.spin.z * dt;
        if (toy.position.y <= r) {
          toy.position.y = r;
          const bounce = this.kind === 'ball' ? 0.55 : this.kind === 'stick' ? 0.15 : 0.1;
          if (this.kind === 'frisbee') this.vel.multiplyScalar(0.5); // it slaps down and skids, no roll
          this.vel.y = -this.vel.y * bounce;
          this.vel.x *= this.kind === 'ball' ? 0.8 : 0.4;
          this.vel.z *= this.kind === 'ball' ? 0.8 : 0.4;
          if (Math.abs(this.vel.y) < 0.6) this.vel.y = 0;
          this.spin.multiplyScalar(0.5);
          if (this.kind === 'stick' && this.vel.y === 0) toy.rotation.set(0, toy.rotation.y, 0);
          if (this.kind === 'labubu' && this.vel.y === 0) toy.rotation.set(-Math.PI / 2, 0, toy.rotation.y); // face up on the grass
        }
      } else {
        // rolling / sliding
        toy.position.y = r;
        const speed = Math.hypot(this.vel.x, this.vel.z);
        if (speed > 0.01) {
          const decel = this.kind === 'ball' ? 1.4 : this.kind === 'stick' ? 6 : 8;
          const s = Math.max(0, speed - decel * dt) / speed;
          this.vel.x *= s;
          this.vel.z *= s;
          toy.position.addScaledVector(this.vel, dt);
          if (this.kind === 'ball') {
            // roll: rotate about the axis perpendicular to travel
            _v.set(this.vel.z, 0, -this.vel.x).normalize();
            toy.rotateOnWorldAxis(_v, (speed * dt) / r);
          }
        } else {
          this.vel.set(0, 0, 0);
        }
      }
      // solid things: trunks, posts, house, store, fence
      if (this.colliders) {
        const speed = this.vel.length();
        const hit = this.colliders.resolve(toy.position, this.vel, r, this.kind === 'ball' ? 0.5 : 0.2);
        if (hit && speed > 0.8 && performance.now() - this.lastHitAt > 250) {
          this.lastHitAt = performance.now();
          this.spin.multiplyScalar(0.4);
          this.onHit?.(hit, speed);
        }
      }
      // keep it on the land
      if (toy.position.length() > 85) {
        toy.position.setLength(85);
        this.vel.multiplyScalar(-0.3);
      }
    }

    // --- her side of it
    const toyRest = !this.inHand && !this.carried && this.vel.lengthSq() < 1e-4 && toy.position.y < 0.1;
    switch (this.state) {
      case 'flying':
        // she takes off after it once it's clearly away; then chases its live position
        this.restTimer += dt;
        if (this.restTimer > 0.25) {
          this.state = 'chasing';
          dog.excitement = 1;
        }
        break;
      case 'chasing': {
        // re-aim at where the toy is (or will be shortly) a few times a second
        if (dog.time > this.retargetAt) {
          _p.copy(toy.position).addScaledVector(this.vel, 0.35).setY(0);
          // aim to stop with her nose over the toy, not her body on top of it
          _v.subVectors(dog.root.position, _p).setY(0);
          const dd = _v.length();
          const reach = 0.7 * dog.profile.size;
          if (dd > reach) _p.addScaledVector(_v, reach / dd);
          dog.goTo(_p, true);
          this.retargetAt = dog.time + (toyRest ? 1 : 0.25);
        }
        const nose = dog.nosePosition(_v);
        const d3 = nose.distanceTo(toy.position);
        // on the ground the toy sits well below her nose, so judge a pick-up in the horizontal plane
        const d = Math.hypot(nose.x - toy.position.x, nose.z - toy.position.z);
        const rootD = Math.hypot(dog.root.position.x - toy.position.x, dog.root.position.z - toy.position.z);
        // a frisbee is meant to be caught in the air: higher, faster, and she'll jump for it
        const frisbee = this.kind === 'frisbee';
        const canCatch = !toyRest && toy.position.y < (frisbee ? 1.05 : 0.45) && d3 < (frisbee ? 0.75 : 0.5) && this.vel.length() < (frisbee ? 9 : 6);
        const within = toyRest && (d < 0.45 || (!dog.moving && rootD < 1.0 * dog.profile.size));
        if (within || (canCatch && Math.random() < (frisbee ? 0.5 : this.catchChance) * dt * 10)) {
          if (this.kind === 'treat' && toyRest) {
            // bend down to it first; the pick-up comes when her nose is there
            dog.reachDown(toy.position, 0.9);
            this.eatAt = dog.time + 0.55;
            this.state = 'reaching';
          } else this.pickUp(!toyRest);
        }
        break;
      }
      case 'reaching': {
        if (dog.time >= this.eatAt) this.pickUp(false);
        break;
      }
      case 'returning': {
        if (!dog.moving) {
          // arrived: drop it, look at you
          this.dropHere();
          dog.lookAt(you);
          dog.excitement = 1;
          this.state = 'idle';
          this.onFetched?.();
          this.onMessage?.(Math.random() < 0.5 ? 'good girl!' : 'again?');
        }
        break;
      }
      case 'idle':
        break;
    }
  }

  private pickUp(caught: boolean): void {
    if (this.kind === 'treat') {
      // gulp: no carrying, no return
      this.toy.visible = false;
      this.inHand = true;
      this.vel.set(0, 0, 0);
      this.dog.grab();
      this.dog.enjoyTreat();
      this.state = 'idle';
      this.onMessage?.(caught ? 'caught it — yum' : 'yum');
      this.onTreatEaten?.();
      return;
    }
    if (caught) {
      if (this.kind === 'frisbee') this.dog.leap();
      this.onCaught?.(this.kind);
    }
    this.carried = true;
    this.lastDistance = Math.hypot(this.toy.position.x - this.thrownFrom.x, this.toy.position.z - this.thrownFrom.z);
    this.vel.set(0, 0, 0);
    this.toy.rotation.set(0, 0, this.kind === 'stick' ? 0 : 0);
    this.dog.carry(this.toy);
    this.dog.grab();
    this.onMessage?.(caught ? 'caught it!' : 'got it');
    // bring it back: to ~1.5 m in front of you, or further if she's feeling cheeky
    const cheeky = Math.random() < 0.25;
    const back = cheeky ? 3 : 1.5;
    _v.copy(this.dog.root.position).sub(this.you).setY(0);
    const d = _v.length();
    if (d > back + 0.2) {
      _v.multiplyScalar(back / d).add(this.you).setY(0);
      this.dog.goTo(_v, true);
    }
    this.state = 'returning';
    this.retargetAt = 0;
  }

  /** where "you" are: set by the loop each frame before update */
  you = new THREE.Vector3();
}
